import React, { useState, useContext, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Box,
  Grid,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { AuthContext } from '../../contexts/AuthContext';
import { ScenarioContext } from '../../contexts/ScenarioContext';
import { useSnackbar } from 'notistack';
import { CheckCircle, Error, Warning } from '@mui/icons-material';
import { motion } from 'framer-motion';

const ScenarioSaveDialog = React.forwardRef(({ open, onClose, analysisData }, ref) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, isAuthenticated } = useContext(AuthContext);
  const { saveScenario, loading } = useContext(ScenarioContext);
  const { enqueueSnackbar } = useSnackbar();

  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) {
      setName('');
      setError('');
      setSuccess(false);
    }
  }, [open]);

const handleSave = async () => {
  if (!name.trim()) {
    setError('Please enter a scenario name');
    return;
  }

  // Enhanced authentication check
  if (!isAuthenticated) {
    const errorMsg = 'Please login to save scenarios';
    setError(errorMsg);
    enqueueSnackbar(errorMsg, { variant: 'error' });
    return;
  }

  if (!user?._id) {
    const errorMsg = 'User session issue. Please refresh the page.';
    setError(errorMsg);
    enqueueSnackbar(errorMsg, { variant: 'error' });
    return;
  }

  if (user?.credits <= 0) {
    const errorMsg = 'You don\'t have enough credits';
    setError(errorMsg);
    enqueueSnackbar(errorMsg, { variant: 'error' });
    return;
  }

  try {
    setSaving(true);
    setError('');
    
    await saveScenario({
      name,
      ...analysisData,
      userId: user._id,
      userEmail: user.email
    });

    setSuccess(true);
    enqueueSnackbar('Scenario saved successfully!', { variant: 'success' });
    
    setTimeout(() => onClose(), 1500);
  } catch (err) {
    // Don't set error if it's a 401 (handled by interceptor)
    if (err.response?.status !== 401) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to save scenario';
      setError(errorMsg);
      enqueueSnackbar(errorMsg, { variant: 'error' });
    }
  } finally {
    setSaving(false);
  }
};





  const getImpactColor = (value) => {
    if (value > 500000) return 'success.main';
    if (value > 100000) return 'info.main';
    return 'warning.main';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      fullScreen={fullScreen}
      ref={ref}
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: theme.palette.background.paper
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center',
          bgcolor: theme.palette.primary.main,
          color: 'white',
          py: 2
        }}>
          {success ? (
            <>
              <CheckCircle sx={{ mr: 1 }} />
              Scenario Saved!
            </>
          ) : (
            'Save Your Scenario'
          )}
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {success ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
              textAlign: 'center'
            }}>
              <CheckCircle 
                color="success" 
                sx={{ fontSize: 60, mb: 2 }} 
              />
              <Typography variant="h6" gutterBottom>
                Scenario saved successfully!
              </Typography>
              <Typography color="text.secondary">
                You can now compare it with other scenarios.
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Potential Impact
                  </Typography>
                  <Typography 
                    variant="h5" 
                    color={getImpactColor(analysisData?.totalImpact || 0)}
                    fontWeight="bold"
                  >
                    ${(analysisData?.totalImpact || 0).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Your Credits
                  </Typography>
                  <Typography 
                    variant="h5" 
                    color={user?.credits > 0 ? 'success.main' : 'error.main'}
                    fontWeight="bold"
                  >
                    {user?.credits || 0} remaining
                  </Typography>
                </Grid>
              </Grid>

              <TextField
                autoFocus
                margin="dense"
                label="Scenario Name"
                fullWidth
                variant="outlined"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError('');
                }}
                error={!!error}
                helperText={error}
                disabled={saving || !isAuthenticated}
                sx={{ mb: 2 }}
                InputProps={{
                  sx: { borderRadius: 2 }
                }}
              />

              {!isAuthenticated && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'warning.light',
                  p: 2,
                  borderRadius: 2,
                  mb: 2
                }}>
                  <Warning color="warning" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    You need to be logged in to save scenarios.
                  </Typography>
                </Box>
              )}

              {isAuthenticated && user?.credits <= 0 && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'error.light',
                  p: 2,
                  borderRadius: 2,
                  mb: 2
                }}>
                  <Error color="error" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    You don't have enough credits to save this scenario.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!success && (
            <Button 
              onClick={onClose} 
              disabled={saving}
              variant="outlined"
              sx={{
                borderRadius: 2,
                px: 3,
                minWidth: 100
              }}
            >
              Cancel
            </Button>
          )}
          
          <Button 
            onClick={success ? onClose : handleSave}
            disabled={saving || !isAuthenticated || user?.credits <= 0}
            color="primary"
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              minWidth: 120,
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground'
              }
            }}
            startIcon={saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : success ? null : (
              <CheckCircle fontSize="small" />
            )}
          >
            {saving ? 'Saving...' : success ? 'Close' : 'Save Scenario'}
          </Button>
        </DialogActions>
      </motion.div>
    </Dialog>
  );
});


ScenarioSaveDialog.displayName = 'ScenarioSaveDialog';

export default ScenarioSaveDialog;
